import 'react-dates/initialize';
import React, { useState, useCallback } from 'react';

import classnames from 'clsx';

import { twelveHourUserSettingsNoTimeZoneUnpadded } from '../../../constants/timezone-format';

import { CalendarDateTimePicker } from '../calendar-date-pickers/calendar-date-time-picker';
import { Dropdown } from '../INTERNAL/dropdown';

import style from './date-time-picker-dropdown.module.css';
import moment from 'moment';

/**
 * DateTimePickerDropdown component
 * @param {object} props
 * @param {string} props.id - the id of the dropdown
 * @param {string} props.value - the value of the date time picker in ISO format with UTC timezone
 * @param {(time: string) => void} props.onChange - function to call when the date time is changed
 * @param {string} [props.dateFormat] - the format to display the date in
 * @param {boolean} [props.allowPastDates] - whether to allow past dates
 * @param {string} [props.className] - the class name of the
 * @param {string} [props.openTogglerClassName] - the class name of the
 * @param {React.ReactNode} props.children - the children of the dropdown
 * @param {DropdownMenuPosition} [props.calendarPosition] - the position of the calendar
 * @param {string} [props.timeFormat] - the format to display the time in
 * @param {number} [props.verticalSpacing] - the vertical spacing of the dropdown
 * @param {string} [props.sellerTimeZone] - the timezone of the seller
 * @returns {React.ReactElement} - the date time picker dropdown component
 */
export const DateTimePickerDropdown = ({
  id,
  value,
  calendarPosition = 'bottom-left',
  className = '',
  openTogglerClassName = '',
  children,
  dateFormat = 'MM/DD/YYYY',
  timeFormat = twelveHourUserSettingsNoTimeZoneUnpadded,
  verticalSpacing = -20,
  sellerTimeZone,
  allowPastDates = false,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [datetime, setDatetime] = useState(value);

  const onClose = useCallback(() => {
    if (isOpen) {
      setIsOpen(false);
      if (datetime) {
        // datetime is in the seller's timezone so we need to convert it to utc
        onChange(moment(datetime).utc().format());
      }
    }
  }, [datetime, isOpen, onChange]);

  const onOpen = useCallback(() => setIsOpen(true), []);

  const change = useCallback((isoDateUtc) => {
    setDatetime(isoDateUtc);
  }, []);

  return (
    <Dropdown
      closeWhenChildrenClicked={false}
      id={id}
      isOpen={isOpen}
      menuPosition={calendarPosition}
      onClose={onClose}
      onOpen={onOpen}
      openTogglerClassName={openTogglerClassName}
    >
      {children}
      <div className={classnames(className, style['single-date-picker'])}>
        <CalendarDateTimePicker
          allowPastDates={allowPastDates}
          dateFormat={dateFormat}
          onChange={change}
          sellerTimeZone={sellerTimeZone}
          shouldAlwaysBeVisible
          timeFormat={timeFormat}
          utcValue={datetime}
          verticalSpacing={verticalSpacing}
        />
      </div>
    </Dropdown>
  );
};
